import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import WaitlistForm from "../components/WaitlistForm";
import GrainOverlay from "../components/GrainOverlay";

const FEATURES = [
  {
    label: "01",
    title: "Private member access",
    body: "Selected drops are shared with GoneSoon members before they are available publicly.",
  },
  {
    label: "02",
    title: "Exclusive local prices",
    body: "Access special prices from places you already love and places worth discovering.",
  },
  {
    label: "03",
    title: "Curated around the city",
    body: "Every participating place is selected and added by the GoneSoon team.",
  },
  {
    label: "04",
    title: "Limited availability",
    body: "Drops may expire, sell out or disappear without notice.",
  },
];

const HOW_STEPS = [
  {
    n: "01",
    title: "Join the club",
    body: "Request early access and become one of the first GoneSoon members in your city.",
  },
  {
    n: "02",
    title: "Unlock exclusive prices",
    body: "Discover private prices from selected cafés, restaurants, experiences, services and local favourites.",
  },
  {
    n: "03",
    title: "Claim before it’s gone",
    body: "Every drop has limited availability. Once it fills up or expires, it disappears.",
  },
];

const FAQS = [
  {
    q: "What is GoneSoon?",
    a: "GoneSoon is a private local savings club where members get access to exclusive prices from selected places around the city.",
  },
  {
    q: "Is GoneSoon a coupon website?",
    a: "No. GoneSoon is a curated membership platform. We release selected local savings as limited drops rather than displaying endless public coupons.",
  },
  {
    q: "Is it free to join?",
    a: "Joining the early-access waitlist is free. Membership details, if introduced later, will be clearly communicated before launch.",
  },
  {
    q: "What is a drop?",
    a: "A drop is a limited opportunity to access a special price from a participating local place. It may be limited by time, quantity or availability.",
  },
  {
    q: "What kinds of places will be available?",
    a: "Drops may come from cafés, restaurants, experiences, activities, wellness services, salons and other selected local businesses.",
  },
  {
    q: "Why are drops limited?",
    a: "GoneSoon is built around special access rather than permanent public pricing. Once the available quantity or time period ends, the drop disappears.",
  },
  {
    q: "Which city is launching first?",
    a: "GoneSoon is launching first in Trivandrum.",
  },
  {
    q: "Will everyone on the waitlist get access immediately?",
    a: "Early access will be released in limited batches. Waitlist members will be considered first when invitations open.",
  },
];

const MARQUEE_WORDS = [
  "Private access",
  "Members only",
  "Limited drop",
  "Selected places",
  "Around the city",
  "Here now",
  "Gone soon",
];

export default function Landing() {
  useEffect(() => {
    // scroll to waitlist section if hash is present on load
    if (window.location.hash === "#waitlist") {
      const el = document.getElementById("waitlist");
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
    document.title = "GoneSoon | Your Private Local Savings Club";
  }, []);

  return (
    <div data-testid="landing-page" className="relative">
      <GrainOverlay />

      {/* ============ HERO (Coral) ============ */}
      <section
        data-testid="hero-section"
        className="bg-coral text-white relative overflow-hidden"
      >
        <Nav variant="coral" />

        <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-24 md:pb-32 relative">
          {/* side vertical label */}
          <div className="hidden lg:block absolute right-10 top-40 vertical-rl text-xs uppercase tracking-[0.35em] text-white/70">
            Private list · Trivandrum · 2026
          </div>

          <div className="flex items-center gap-3 mb-10 animate-fade-up">
            <div className="pill text-white border-white/60" data-testid="hero-pill">
              <span className="pill-dot" />
              Members-only · Coming soon
            </div>
          </div>

          <h1
            data-testid="hero-headline"
            className="h-display text-white text-[3.25rem] sm:text-6xl md:text-[6.5rem] lg:text-[8.5rem] max-w-[13ch] animate-fade-up"
            style={{ animationDelay: "80ms" }}
          >
            Your private access to better local prices.
          </h1>

          <div className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
            <p
              className="md:col-span-6 text-white/90 text-lg md:text-xl leading-relaxed max-w-lg animate-fade-up"
              style={{ animationDelay: "180ms" }}
            >
              Exclusive prices from selected places around the city.<br />
              Limited drops. Once gone, they’re gone.
            </p>

            <div
              className="md:col-span-6 md:justify-self-end flex flex-col items-start md:items-end gap-4 animate-fade-up"
              style={{ animationDelay: "260ms" }}
            >
              <a
                href="#waitlist"
                data-testid="hero-cta-join"
                className="btn-primary btn-on-coral"
              >
                Join the private waitlist
                <span aria-hidden="true">→</span>
              </a>
              <p className="text-xs text-white/70 uppercase tracking-[0.22em]">
                Early members get first access.
              </p>
            </div>
          </div>

          <div
            className="mt-20 md:mt-28 flex items-center gap-6 text-white/90 animate-fade-up"
            style={{ animationDelay: "340ms" }}
          >
            <div className="h-px flex-1 bg-white/25" />
            <span className="text-xs uppercase tracking-[0.28em] whitespace-nowrap">
              Coming soon to Trivandrum. 🥂
            </span>
            <div className="h-px flex-1 bg-white/25" />
          </div>
        </div>
      </section>

      {/* ============ MARQUEE ============ */}
      <section
        aria-hidden="true"
        data-testid="marquee-section"
        className="bg-ink text-cream py-8 overflow-hidden border-y border-white/5"
      >
        <div className="marquee-track font-cabinet font-black text-3xl md:text-5xl uppercase tracking-tighter">
          {[...MARQUEE_WORDS, ...MARQUEE_WORDS, ...MARQUEE_WORDS].map((w, i) => (
            <span key={i} className="flex items-center gap-16">
              <span>{w}</span>
              <span className="text-coral">✦</span>
            </span>
          ))}
        </div>
      </section>

      {/* ============ HOW IT WORKS ============ */}
      <section
        data-testid="how-it-works"
        id="how"
        className="bg-cream text-ink"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-36">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16 md:mb-24">
            <div className="md:col-span-4">
              <div className="pill text-ink/70 border-ink/30 mb-6" data-testid="how-pill">
                How it works
              </div>
            </div>
            <div className="md:col-span-8">
              <h2 className="h-display text-4xl sm:text-5xl md:text-7xl">
                Private access.<br />
                Local savings.<br />
                <span className="text-coral">Limited time.</span>
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-16">
            {HOW_STEPS.map((s, i) => (
              <div
                key={s.n}
                data-testid={`how-step-${i + 1}`}
                className="border-t border-ink/15 pt-8"
              >
                <div className="font-cabinet font-black text-5xl md:text-6xl text-coral leading-none">
                  {s.n}
                </div>
                <h3 className="mt-10 h-display text-2xl md:text-3xl">{s.title}</h3>
                <p className="mt-4 text-ink/70 text-base leading-relaxed max-w-xs">
                  {s.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ MEMBERSHIP (Dark) ============ */}
      <section
        data-testid="membership-section"
        className="relative bg-ink text-cream overflow-hidden"
      >
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-25 pointer-events-none"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1585314062340-f1a5a7c9328d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwyfHxibGFjayUyMHRleHR1cmVkJTIwYWJzdHJhY3QlMjBzdXJmYWNlJTIwZGFya3xlbnwwfHx8fDE3ODQxMjQ0ODF8MA&ixlib=rb-4.1.0&q=85')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            mixBlendMode: "screen",
          }}
        />
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-40 relative">
          <div className="pill text-cream/70 border-cream/30 mb-10" data-testid="membership-pill">
            <span className="pill-dot" />
            Members only
          </div>
          <h2 className="h-display text-4xl sm:text-6xl md:text-[6.5rem] leading-[0.95] max-w-[14ch]">
            Not available <span className="text-coral">to everyone.</span><br />
            Not available <span className="text-coral">forever.</span>
          </h2>

          <div className="mt-16 md:mt-24 grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-5 md:col-start-6 space-y-6 text-cream/80 text-base md:text-lg leading-relaxed">
              <p>
                GoneSoon is built for people who love discovering the city without always paying the usual price.
              </p>
              <p>
                We partner with selected local places to release special prices privately to our members.
              </p>
              <ul className="space-y-3 pt-4 border-t border-cream/20">
                <li className="flex items-baseline gap-3">
                  <span className="text-coral font-cabinet font-black">—</span>
                  <span>No endless coupon lists.</span>
                </li>
                <li className="flex items-baseline gap-3">
                  <span className="text-coral font-cabinet font-black">—</span>
                  <span>No outdated offers.</span>
                </li>
                <li className="flex items-baseline gap-3">
                  <span className="text-coral font-cabinet font-black">—</span>
                  <span>No public clutter.</span>
                </li>
              </ul>
              <p className="pt-4 text-cream">
                Just limited local drops worth opening in time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FEATURES ============ */}
      <section
        data-testid="features-section"
        className="bg-cream text-ink"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-36">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16">
            <div className="md:col-span-4">
              <div className="pill text-ink/70 border-ink/30 mb-6">Why members join</div>
            </div>
            <div className="md:col-span-8">
              <h2 className="h-display text-4xl md:text-6xl max-w-[16ch]">
                A quieter way to know your city.
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-16">
            {FEATURES.map((f) => (
              <div
                key={f.label}
                data-testid={`feature-${f.label}`}
                className="group"
              >
                <div className="flex items-baseline gap-6 border-b border-ink/15 pb-6">
                  <span className="font-cabinet font-black text-coral text-3xl">{f.label}</span>
                  <h3 className="h-display text-2xl md:text-3xl">{f.title}</h3>
                </div>
                <p className="mt-6 text-ink/70 text-base leading-relaxed max-w-md">
                  {f.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ WAITLIST FORM ============ */}
      <section
        data-testid="waitlist-section"
        id="waitlist"
        className="bg-cream text-ink border-t border-ink/10"
      >
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-24 md:py-36">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-start">
            <div className="md:col-span-5">
              <div className="pill text-ink/70 border-ink/30 mb-8" data-testid="waitlist-pill">
                <span className="pill-dot" />
                Private access
              </div>
              <h2 className="h-display text-4xl md:text-6xl leading-[0.95]">
                Get on the<br />private list.
              </h2>
              <p className="mt-8 text-ink/70 leading-relaxed max-w-sm">
                Join the first group of members getting access to limited local drops in Trivandrum.
              </p>
              <div className="mt-10 space-y-2 text-xs uppercase tracking-[0.22em] text-ink/50">
                <div>Batch 01 · Trivandrum</div>
                <div>Invitations released in waves</div>
              </div>
            </div>
            <div className="md:col-span-7 md:pl-8 md:border-l border-ink/10">
              <WaitlistForm />
            </div>
          </div>
        </div>
      </section>

      {/* ============ LAUNCH ============ */}
      <section
        data-testid="launch-section"
        className="relative bg-coral text-white overflow-hidden"
      >
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-20 pointer-events-none mix-blend-multiply"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1776087865696-a03f970ed1f4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGFyY2hpdGVjdHVyYWwlMjBkZXRhaWwlMjBtaW5pbWFsaXN0JTIwc2hhZG93c3xlbnwwfHx8fDE3ODQxMjQ0NDF8MA&ixlib=rb-4.1.0&q=85')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-40 relative">
          <div className="pill text-white border-white/60 mb-10">
            <span className="pill-dot" />
            Limited drop · Trivandrum
          </div>
          <h2 className="h-display text-white text-5xl md:text-[7rem] leading-[0.92] max-w-[14ch]">
            Trivandrum,<br />
            your private invite is coming.
          </h2>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
            <p className="md:col-span-6 text-white/90 text-lg md:text-xl leading-relaxed max-w-lg">
              Be among the first members to unlock exclusive prices from selected places around the city.
            </p>
            <div className="md:col-span-6 md:justify-self-end flex flex-col md:items-end gap-3">
              <a
                href="#waitlist"
                data-testid="launch-cta"
                className="btn-primary btn-on-coral"
              >
                Join the private waitlist
                <span aria-hidden="true">→</span>
              </a>
              <p className="text-xs text-white/70 uppercase tracking-[0.22em]">
                Launching soon. Limited early access. 🥂
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FAQ ============ */}
      <section
        data-testid="faq-section"
        id="faq"
        className="bg-cream text-ink"
      >
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-24 md:py-36">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-16">
            <div className="md:col-span-4">
              <div className="pill text-ink/70 border-ink/30 mb-6">FAQ</div>
              <h2 className="h-display text-4xl md:text-6xl leading-[0.95]">
                Small<br />print.<br />
                <span className="text-coral">Big trust.</span>
              </h2>
            </div>
            <div className="md:col-span-8">
              <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
                {FAQS.map((item, i) => (
                  <AccordionItem
                    key={i}
                    value={`faq-${i}`}
                    className="border-b border-ink/15"
                    data-testid={`faq-item-${i}`}
                  >
                    <AccordionTrigger
                      data-testid={`faq-trigger-${i}`}
                      className="text-left h-display text-xl md:text-2xl py-6 hover:no-underline"
                    >
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-ink/70 text-base leading-relaxed pb-8 max-w-2xl">
                      {item.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
